import { useEffect, useRef } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  Badge,
  Body1,
  Button,
  Caption1,
  Card,
  MessageBar,
  MessageBarBody,
  MessageBarTitle,
  ProgressBar,
  Spinner,
  Subtitle1,
  Subtitle2,
  makeStyles,
  mergeClasses,
  tokens,
} from '@fluentui/react-components';
import type { StepRecord } from '@lg/core';
import { hasAnyRole, type StepKey, type StepStatus } from '@lg/shared';
import { useApi } from '../api/client';
import { useAuth } from '../auth/AuthContext';
import { dateTime, errorMessage } from '../ui/format';

export const STEP_TITLES: Record<StepKey, { title: string; what: string }> = {
  preflight: {
    title: 'Preflight',
    what: 'Re-run gate 1; check the template company, copy project and people project exist',
  },
  createLegalEntity: { title: 'Create legal entity', what: 'Create the company in D365' },
  addressAndRegistration: { title: 'Address and registration', what: 'Primary address and tax registration' },
  copyFromTemplate: {
    title: 'Copy from template',
    what: 'Run the saved Copy into legal entity project from the template company',
  },
  venueOverrides: { title: 'Venue overrides', what: 'Write the answers that differ from the template' },
  importPeople: {
    title: 'Import people and approvals',
    what: 'Send the people package to the recurring data job and wait for it',
  },
  assignSecurity: { title: 'Assign security', what: 'Role assignments scoped to the new company only' },
  importCustomersVendors: {
    title: 'Create customers and vendors',
    what: 'Customers and vendors tabs, created one record at a time over OData',
  },
  importProjects: {
    title: 'Create projects',
    what: 'Project contracts with their customer funding source, then the opening projects',
  },
  policiesAndParameters: {
    title: 'Policies and parameters',
    what: 'Signing limit and purchasing policies, Require explicitly assigned users',
  },
  importWorkflows: { title: 'Import workflows', what: 'Template approval workflows, XML unchanged' },
  validate: { title: 'Validate', what: 'Build the validation report on data read back from D365' },
  notify: { title: 'Notify', what: 'Tell the requester, approvers and administrator' },
};

const STATUS_COLOR: Record<StepStatus, 'informative' | 'brand' | 'success' | 'danger' | 'subtle'> = {
  Pending: 'subtle',
  Running: 'brand',
  Succeeded: 'success',
  Skipped: 'informative',
  Failed: 'danger',
};

const useStyles = makeStyles({
  stack: { display: 'flex', flexDirection: 'column', gap: tokens.spacingVerticalM },
  card: {
    padding: tokens.spacingHorizontalL,
    display: 'flex',
    flexDirection: 'column',
    gap: tokens.spacingVerticalS,
  },
  head: { display: 'flex', alignItems: 'center', gap: tokens.spacingHorizontalM, flexWrap: 'wrap' },
  spacer: { flex: 1 },
  steps: { display: 'flex', flexDirection: 'column' },
  step: {
    display: 'grid',
    gridTemplateColumns: '32px 1fr auto',
    gap: tokens.spacingHorizontalM,
    padding: `${tokens.spacingVerticalS} 0`,
    borderBottom: `1px solid ${tokens.colorNeutralStroke3}`,
    alignItems: 'start',
  },
  running: { backgroundColor: tokens.colorBrandBackground2 },
  failed: { backgroundColor: tokens.colorPaletteRedBackground1 },
  num: { textAlign: 'center', color: tokens.colorNeutralForeground3, paddingTop: '2px' },
  body: { display: 'flex', flexDirection: 'column', gap: tokens.spacingVerticalXXS, minWidth: 0 },
  output: {
    fontSize: tokens.fontSizeBase200,
    color: tokens.colorNeutralForeground2,
    overflowWrap: 'anywhere',
  },
  mono: { fontFamily: tokens.fontFamilyMonospace, fontSize: tokens.fontSizeBase200 },
  right: { display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: tokens.spacingVerticalXXS },
});

function summarise(output: Record<string, unknown> | null): string {
  if (!output) return '';
  return (
    Object.entries(output)
      // templateCounts is the preflight snapshot behind "templateSnapshot"; the report shows the comparison.
      .filter(([k]) => k !== 'report' && k !== 'progress' && k !== 'templateCounts')
      .map(
        ([k, v]) =>
          `${k}: ${Array.isArray(v) ? v.join(', ') || '—' : typeof v === 'object' ? JSON.stringify(v) : String(v)}`,
      )
      .join(' · ')
  );
}

/** Provisioning tracker (spec 15.2): ordered steps, status, times, D365 job IDs, errors, Resume. */
export function ProvisioningTab({ requestId }: { requestId: string }) {
  const styles = useStyles();
  const api = useApi();
  const qc = useQueryClient();
  const { user } = useAuth();
  const run = useQuery({
    queryKey: ['run', requestId],
    queryFn: () => api.getLatestRun(requestId),
    refetchInterval: (q) => (q.state.data?.run.status === 'Running' ? 1000 : false),
  });
  const resume = useMutation({
    mutationFn: (runId: string) => api.resumeRun(runId),
    onSuccess: () => {
      void run.refetch();
      void qc.invalidateQueries({ queryKey: ['request', requestId] });
    },
  });

  // Refresh the request (status badge, tabs) whenever the run status changes.
  const lastStatus = useRef<string | undefined>(undefined);
  const status = run.data?.run.status;
  useEffect(() => {
    if (status && lastStatus.current && status !== lastStatus.current) {
      void qc.invalidateQueries({ queryKey: ['request', requestId] });
      void qc.invalidateQueries({ queryKey: ['requests'] });
      void qc.invalidateQueries({ queryKey: ['report', requestId] });
    }
    lastStatus.current = status;
  }, [status, qc, requestId]);

  if (run.isPending) return <Spinner label="Loading run…" />;
  if (run.isError)
    return (
      <MessageBar intent="error">
        <MessageBarBody>{errorMessage(run.error)}</MessageBarBody>
      </MessageBar>
    );
  if (!run.data) {
    return (
      <Card className={styles.card}>
        <Body1>
          Provisioning has not started. It starts when the D365 administrator confirms gate 4 on the Approvals
          tab.
        </Body1>
      </Card>
    );
  }
  const { steps, liveSteps } = run.data;
  const r = run.data.run;
  const finished = steps.filter((s) => s.status === 'Succeeded' || s.status === 'Skipped').length;
  const failed = steps.find((s) => s.status === 'Failed');

  return (
    <div className={styles.stack}>
      <Card className={styles.card}>
        <div className={styles.head}>
          <Subtitle1 as="h2">Provisioning run</Subtitle1>
          <Badge
            appearance="tint"
            color={r.status === 'Succeeded' ? 'success' : r.status === 'NeedsAttention' ? 'danger' : 'brand'}
          >
            {r.status === 'NeedsAttention' ? 'Needs attention' : r.status}
          </Badge>
          <Caption1>
            Started {dateTime(r.startedAt)} by {r.startedBy}
            {r.endedAt ? ` · finished ${dateTime(r.endedAt)}` : ''}
          </Caption1>
          <span className={styles.spacer} />
          {r.status === 'NeedsAttention' && hasAnyRole(user, ['D365Admin']) && (
            <Button appearance="primary" disabled={resume.isPending} onClick={() => resume.mutate(r.id)}>
              Resume from failed step
            </Button>
          )}
        </div>
        <ProgressBar
          value={finished / steps.length}
          thickness="large"
          color={failed ? 'error' : r.status === 'Succeeded' ? 'success' : 'brand'}
        />
        <Caption1>
          {finished} of {steps.length} steps finished · instance{' '}
          <span className={styles.mono}>{r.orchestrationInstanceId}</span>
        </Caption1>
        {failed && (
          <MessageBar intent="error" layout="multiline">
            <MessageBarBody>
              <MessageBarTitle>
                {STEP_TITLES[failed.stepKey].title} failed (attempt {failed.attempt})
              </MessageBarTitle>
              {failed.error} Nothing after this step has run. Fix the cause in D365 if needed, then resume;
              completed steps are not repeated.
            </MessageBarBody>
          </MessageBar>
        )}
        {resume.isError && (
          <MessageBar intent="error">
            <MessageBarBody>{errorMessage(resume.error)}</MessageBarBody>
          </MessageBar>
        )}
      </Card>

      <Card className={styles.card}>
        <div className={styles.steps}>
          {steps.map((s) => (
            <StepRow
              key={s.id}
              step={s}
              live={liveSteps.includes(s.stepKey)}
              showMode={liveSteps.length > 0}
            />
          ))}
        </div>
      </Card>
    </div>
  );
}

function StepRow({ step: s, live, showMode }: { step: StepRecord; live: boolean; showMode: boolean }) {
  const styles = useStyles();
  const t = STEP_TITLES[s.stepKey];
  const progress = typeof s.output?.['progress'] === 'string' ? (s.output['progress'] as string) : null;
  return (
    <div
      className={mergeClasses(
        styles.step,
        s.status === 'Running' && styles.running,
        s.status === 'Failed' && styles.failed,
      )}
    >
      <span className={styles.num}>{s.sequence}</span>
      <div className={styles.body}>
        <Subtitle2>{t.title}</Subtitle2>
        <Caption1>{t.what}</Caption1>
        {s.status === 'Running' && (
          <Spinner size="extra-tiny" label={progress ?? 'Working…'} labelPosition="after" />
        )}
        {s.status !== 'Running' && s.output && <span className={styles.output}>{summarise(s.output)}</span>}
        {s.d365ExecutionId && (
          <span className={styles.output}>
            D365 job{s.d365ExecutionId.includes(',') ? 's' : ''}:{' '}
            <span className={styles.mono}>{s.d365ExecutionId.split(',').join(', ')}</span>
          </span>
        )}
        {s.error && (
          <span className={styles.output} style={{ color: tokens.colorPaletteRedForeground1 }}>
            {s.error}
          </span>
        )}
      </div>
      <div className={styles.right}>
        <Badge appearance="tint" color={STATUS_COLOR[s.status]}>
          {s.status}
        </Badge>
        {/* Live slice: which steps wrote to the real D365 sandbox. */}
        {showMode &&
          (live ? (
            <Badge appearance="filled" color="brand" size="small">
              Live · D365
            </Badge>
          ) : (
            <Badge appearance="outline" color="informative" size="small">
              Simulated
            </Badge>
          ))}
        {s.startedAt && (
          <Caption1>
            {dateTime(s.startedAt)}
            {s.attempt > 1 ? ` · attempt ${s.attempt}` : ''}
          </Caption1>
        )}
      </div>
    </div>
  );
}
